import React, { useState, useRef, useMemo, useEffect, memo } from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

// Common city coordinates lookup [lng, lat]
const CITY_COORDS: Record<string, [number, number]> = {
  // UK & Ireland
  'london': [-0.1276, 51.5074],
  'manchester': [-2.2426, 53.4808],
  'birmingham': [-1.8904, 52.4862],
  'leeds': [-1.5491, 53.8008],
  'glasgow': [-4.2518, 55.8642],
  'edinburgh': [-3.1883, 55.9533],
  'bristol': [-2.5879, 51.4545],
  'liverpool': [-2.9916, 53.4084],
  'sheffield': [-1.4701, 53.3811],
  'newcastle': [-1.6178, 54.9783],
  'cardiff': [-3.1791, 51.4816],
  'belfast': [-5.9301, 54.5973],
  'nottingham': [-1.1581, 52.9548],
  'cambridge': [0.1218, 52.2053],
  'oxford': [-1.2577, 51.7520],
  'brighton': [-0.1373, 50.8225],
  'reading': [-0.9781, 51.4543],
  'southampton': [-1.4044, 50.9097],
  'leicester': [-1.1398, 52.6369],
  'coventry': [-1.5197, 52.4068],
  'dublin': [-6.2603, 53.3498],
  'cork': [-8.4863, 51.8985],
  'bath': [-2.3599, 51.3811],
  'york': [-1.0827, 53.9591],
  'aberdeen': [-2.0943, 57.1497],
  'exeter': [-3.5339, 50.7184],
  'swindon': [-1.7797, 51.5558],
  'milton keynes': [-0.7594, 52.0406],
  'northampton': [-0.9027, 52.2405],
  'warwick': [-1.5849, 52.2820],
  'guildford': [-0.5703, 51.2362],
  'cheltenham': [-2.0749, 51.8994],
  // Europe
  'paris': [2.3522, 48.8566],
  'berlin': [13.4050, 52.5200],
  'amsterdam': [4.9041, 52.3676],
  'munich': [11.5820, 48.1351],
  'madrid': [-3.7038, 40.4168],
  'barcelona': [2.1734, 41.3851],
  'rome': [12.4964, 41.9028],
  'milan': [9.1900, 45.4642],
  'vienna': [16.3738, 48.2082],
  'zurich': [8.5417, 47.3769],
  'stockholm': [18.0686, 59.3293],
  'copenhagen': [12.5683, 55.6761],
  'oslo': [10.7522, 59.9139],
  'helsinki': [24.9384, 60.1699],
  'warsaw': [21.0122, 52.2297],
  'prague': [14.4378, 50.0755],
  'budapest': [19.0402, 47.4979],
  'lisbon': [-9.1393, 38.7223],
  'brussels': [4.3517, 50.8503],
  'geneva': [6.1432, 46.2044],
  'lyon': [4.8357, 45.7640],
  'frankfurt': [8.6821, 50.1109],
  'hamburg': [9.9937, 53.5511],
  'rotterdam': [4.4777, 51.9244],
  // US
  'new york': [-74.0060, 40.7128],
  'new york city': [-74.0060, 40.7128],
  'los angeles': [-118.2437, 34.0522],
  'chicago': [-87.6298, 41.8781],
  'san francisco': [-122.4194, 37.7749],
  'seattle': [-122.3321, 47.6062],
  'boston': [-71.0589, 42.3601],
  'austin': [-97.7431, 30.2672],
  'denver': [-104.9903, 39.7392],
  'atlanta': [-84.3880, 33.7490],
  'miami': [-80.1918, 25.7617],
  'washington': [-77.0369, 38.9072],
  'dallas': [-96.7970, 32.7767],
  'houston': [-95.3698, 29.7604],
  'phoenix': [-112.0740, 33.4484],
  'portland': [-122.6765, 45.5152],
  'san diego': [-117.1611, 32.7157],
  'san jose': [-121.8863, 37.3382],
  'philadelphia': [-75.1652, 39.9526],
  'minneapolis': [-93.2650, 44.9778],
  'charlotte': [-80.8431, 35.2271],
  'raleigh': [-78.6382, 35.7796],
  'nashville': [-86.7816, 36.1627],
  // Canada
  'toronto': [-79.3832, 43.6532],
  'vancouver': [-123.1216, 49.2827],
  'montreal': [-73.5673, 45.5017],
  'calgary': [-114.0719, 51.0447],
  'ottawa': [-75.6972, 45.4215],
  // Australia & NZ
  'sydney': [151.2093, -33.8688],
  'melbourne': [144.9631, -37.8136],
  'brisbane': [153.0251, -27.4698],
  'perth': [115.8605, -31.9505],
  'auckland': [174.7633, -36.8485],
  'adelaide': [138.6007, -34.9285],
  // Asia
  'singapore': [103.8198, 1.3521],
  'hong kong': [114.1694, 22.3193],
  'tokyo': [139.6917, 35.6895],
  'mumbai': [72.8777, 19.0760],
  'bangalore': [77.5946, 12.9716],
  'bengaluru': [77.5946, 12.9716],
  'delhi': [77.1025, 28.7041],
  'new delhi': [77.1025, 28.7041],
  'hyderabad': [78.4867, 17.3850],
  'shanghai': [121.4737, 31.2304],
  'beijing': [116.4074, 39.9042],
  'dubai': [55.2708, 25.2048],
  'tel aviv': [34.7818, 32.0853],
  'seoul': [126.9780, 37.5665],
  'kuala lumpur': [101.6869, 3.1390],
  'jakarta': [106.8456, -6.2088],
  'manila': [120.9842, 14.5995],
  'bangkok': [100.5018, 13.7563],
  // Africa
  'cape town': [18.4241, -33.9249],
  'johannesburg': [28.0473, -26.2041],
  'nairobi': [36.8219, -1.2921],
  'lagos': [3.3792, 6.5244],
  'cairo': [31.2357, 30.0444],
  // South America
  'sao paulo': [-46.6333, -23.5505],
  'buenos aires': [-58.3816, -34.6037],
  'mexico city': [-99.1332, 19.4326],
  'bogota': [-74.0721, 4.7110],
  'santiago': [-70.6693, -33.4489],
  'lima': [-77.0428, -12.0464],
};

interface GeoDataItem {
  city: string;
  count: number;
}

interface GeoMapProps {
  data: GeoDataItem[];
  height?: number;
  maxMarkers?: number;
  className?: string;
}

function lookupCoords(cityName: string): [number, number] | null {
  const normalized = cityName.toLowerCase().trim();
  if (CITY_COORDS[normalized]) return CITY_COORDS[normalized];
  for (const [key, coords] of Object.entries(CITY_COORDS)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return coords;
    }
  }
  return null;
}

// ── Mercator projection ──────────────────────────────────────────
const MAP_W = 800;
const MAP_H = 500;
const LAT_LIMIT = 80;

function toMercator(lng: number, lat: number): [number, number] {
  const clampedLat = Math.max(-LAT_LIMIT, Math.min(LAT_LIMIT, lat));
  const x = (lng + 180) / 360 * MAP_W;
  const latRad = (clampedLat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = MAP_H / 2 - (MAP_W * mercN) / (2 * Math.PI);
  return [x, y];
}

// ── Minimal TopoJSON decoder ─────────────────────────────────────
interface TopoData {
  type: string;
  objects: Record<string, { type: string; geometries: any[] }>;
  arcs: number[][][];
  transform?: { scale: [number, number]; translate: [number, number] };
}

function decodeTopoJSON(topo: TopoData): string[] {
  const { arcs, transform } = topo;

  // Decode delta-encoded, quantized arcs → [lng, lat][]
  const decodedArcs: number[][][] = arcs.map(arc => {
    const pts: number[][] = [];
    let x = 0, y = 0;
    for (const pt of arc) {
      x += pt[0];
      y += pt[1];
      if (transform) {
        pts.push([
          x * transform.scale[0] + transform.translate[0],
          y * transform.scale[1] + transform.translate[1],
        ]);
      } else {
        pts.push([x, y]);
      }
    }
    return pts;
  });

  // Resolve an arc index to coordinates (negative = reversed)
  const resolveArc = (idx: number): number[][] => {
    if (idx >= 0) return decodedArcs[idx];
    return [...decodedArcs[~idx]].reverse();
  };

  // Convert a ring (array of arc indices) to an SVG path segment
  const ringToPath = (arcIndices: number[]): string => {
    const coords: number[][] = [];
    for (const idx of arcIndices) {
      const arcPts = resolveArc(idx);
      // Skip first point if it duplicates the last added point
      const start = coords.length > 0 ? 1 : 0;
      for (let i = start; i < arcPts.length; i++) {
        coords.push(arcPts[i]);
      }
    }
    if (coords.length === 0) return '';
    const projected = coords.map(([lng, lat]) => toMercator(lng, lat));
    return 'M' + projected.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join('L') + 'Z';
  };

  // Extract all polygons from the first object (usually "countries")
  const paths: string[] = [];
  const collection = Object.values(topo.objects)[0];
  if (!collection || !collection.geometries) return paths;

  for (const geom of collection.geometries) {
    if (geom.type === 'Polygon') {
      // geom.arcs = [[ring0ArcIndices], [ring1ArcIndices], ...]
      for (const ring of geom.arcs) {
        const p = ringToPath(ring);
        if (p) paths.push(p);
      }
    } else if (geom.type === 'MultiPolygon') {
      // geom.arcs = [[[ring0], [ring1]], [[ring0]], ...]
      for (const polygon of geom.arcs) {
        for (const ring of polygon) {
          const p = ringToPath(ring);
          if (p) paths.push(p);
        }
      }
    }
  }

  return paths;
}

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const GeoMap: React.FC<GeoMapProps> = memo(({
  data,
  height = 420,
  maxMarkers = 30,
  className = '',
}) => {
  const [countryPaths, setCountryPaths] = useState<string[]>([]);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load and decode world map data
  useEffect(() => {
    let cancelled = false;
    fetch(GEO_URL)
      .then(r => r.json())
      .then((topo: TopoData) => {
        if (!cancelled) setCountryPaths(decodeTopoJSON(topo));
      })
      .catch(() => {}); // Graceful fallback — markers still render
    return () => { cancelled = true; };
  }, []);

  const maxCount = Math.max(...data.map(d => d.count), 1);
  const total = data.reduce((s, d) => s + d.count, 0);

  // Resolve city coordinates
  const markers = useMemo(() =>
    data
      .slice(0, maxMarkers)
      .map(d => ({ ...d, coords: lookupCoords(d.city) }))
      .filter((d): d is typeof d & { coords: [number, number] } => d.coords !== null),
    [data, maxMarkers]
  );

  // Project markers to SVG coordinates
  const projected = useMemo(() =>
    markers.map(m => ({
      ...m,
      pos: toMercator(m.coords[0], m.coords[1]),
    })),
    [markers]
  );

  const resolvedCount = markers.length;
  const unmatchedCount = Math.min(data.length, maxMarkers) - resolvedCount;

  // Auto-zoom: fit the region containing the bulk of candidates (density-based)
  const { viewBox, scale, outlierCount, outlierCities } = useMemo(() => {
    if (projected.length === 0) {
      return { viewBox: `0 0 ${MAP_W} ${MAP_H}`, scale: 1, outlierCount: 0, outlierCities: 0 };
    }

    // If 3 or fewer markers, just fit them all
    if (projected.length <= 3) {
      const xs = projected.map(m => m.pos[0]);
      const ys = projected.map(m => m.pos[1]);
      const pad = 25;
      let minX = Math.min(...xs) - pad, maxX = Math.max(...xs) + pad;
      let minY = Math.min(...ys) - pad, maxY = Math.max(...ys) + pad;
      let w = maxX - minX, h = maxY - minY;
      if (w < 120) { const cx = (minX + maxX) / 2; minX = cx - 60; w = 120; }
      if (h < 80) { const cy = (minY + maxY) / 2; minY = cy - 40; h = 80; }
      const targetAspect = MAP_W / MAP_H;
      if (w / h > targetAspect) { const nh = w / targetAspect; minY -= (nh - h) / 2; h = nh; }
      else { const nw = h * targetAspect; minX -= (nw - w) / 2; w = nw; }
      return { viewBox: `${minX} ${minY} ${w} ${h}`, scale: w / MAP_W, outlierCount: 0, outlierCities: 0 };
    }

    // Weighted center of all markers
    const wcx = projected.reduce((s, m) => s + m.pos[0] * m.count, 0) / total;
    const wcy = projected.reduce((s, m) => s + m.pos[1] * m.count, 0) / total;

    // Sort markers by distance from weighted center
    const sorted = [...projected].sort((a, b) => {
      const da = (a.pos[0] - wcx) ** 2 + (a.pos[1] - wcy) ** 2;
      const db = (b.pos[0] - wcx) ** 2 + (b.pos[1] - wcy) ** 2;
      return da - db;
    });

    // Include markers until we cover >= 90% of total candidates
    const threshold = total * 0.9;
    let cumCount = 0;
    let includeIdx = 0;
    for (let i = 0; i < sorted.length; i++) {
      cumCount += sorted[i].count;
      includeIdx = i;
      if (cumCount >= threshold) break;
    }
    // Always include at least all markers within the same distance as the last included
    const lastDist = (sorted[includeIdx].pos[0] - wcx) ** 2 + (sorted[includeIdx].pos[1] - wcy) ** 2;
    while (includeIdx < sorted.length - 1) {
      const nextDist = (sorted[includeIdx + 1].pos[0] - wcx) ** 2 + (sorted[includeIdx + 1].pos[1] - wcy) ** 2;
      if (nextDist <= lastDist * 1.3) { includeIdx++; } // 30% tolerance
      else break;
    }

    const included = sorted.slice(0, includeIdx + 1);
    const excluded = sorted.slice(includeIdx + 1);
    const outlierCandidates = excluded.reduce((s, m) => s + m.count, 0);

    const xs = included.map(m => m.pos[0]);
    const ys = included.map(m => m.pos[1]);
    const pad = 25;

    let minX = Math.min(...xs) - pad;
    let maxX = Math.max(...xs) + pad;
    let minY = Math.min(...ys) - pad;
    let maxY = Math.max(...ys) + pad;

    let w = maxX - minX;
    let h = maxY - minY;

    // Minimum extent
    if (w < 120) { const cx = (minX + maxX) / 2; minX = cx - 60; w = 120; }
    if (h < 80) { const cy = (minY + maxY) / 2; minY = cy - 40; h = 80; }

    // Maintain aspect ratio
    const targetAspect = MAP_W / MAP_H;
    const currentAspect = w / h;
    if (currentAspect > targetAspect) {
      const newH = w / targetAspect;
      minY -= (newH - h) / 2;
      h = newH;
    } else {
      const newW = h * targetAspect;
      minX -= (newW - w) / 2;
      w = newW;
    }

    return {
      viewBox: `${minX} ${minY} ${w} ${h}`,
      scale: w / MAP_W,
      outlierCount: outlierCandidates,
      outlierCities: excluded.length,
    };
  }, [projected, total]);

  const handleMouseEnter = (m: typeof projected[0], e: React.MouseEvent) => {
    setHoveredCity(m.city);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 8,
        label: m.city,
        value: m.count,
      });
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ height }}>
      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', borderRadius: 8 }}
      >
        {/* Ocean background */}
        <rect x={-200} y={-200} width={MAP_W + 400} height={MAP_H + 400} fill="#F8FAFC" />

        {/* Country polygons */}
        {countryPaths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill={SURFACE.borderLight}
            stroke={SURFACE.border}
            strokeWidth={0.3 * scale}
            strokeLinejoin="round"
          />
        ))}

        {/* Data markers */}
        {projected.map((m, i) => {
          const sizePct = m.count / maxCount;
          const r = (3 + sizePct * 14) * scale;
          const color = CHART_PALETTE[0];
          const isHovered = hoveredCity === m.city;

          return (
            <g
              key={m.city}
              transform={`translate(${m.pos[0]},${m.pos[1]})`}
              onMouseEnter={(e) => handleMouseEnter(m, e)}
              onMouseLeave={() => { setHoveredCity(null); setTooltip(null); }}
              style={{ cursor: 'pointer' }}
            >
              {/* Pulse ring for top 3 */}
              {i < 3 && (
                <circle
                  r={r + 4 * scale}
                  fill="none"
                  stroke={color.base}
                  strokeWidth={1 * scale}
                  opacity={0.3}
                >
                  <animate attributeName="r" from={r + 2 * scale} to={r + 10 * scale} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
              {/* Main dot */}
              <circle
                r={isHovered ? r + 2 * scale : r}
                fill={color.gradient[0]}
                stroke="white"
                strokeWidth={1.5 * scale}
                opacity={isHovered ? 1 : 0.8}
                style={{ transition: 'all 0.15s ease' }}
              />
              {/* City label for large markers */}
              {r / scale > 10 && !isHovered && (
                <text
                  textAnchor="middle"
                  y={r + 10 * scale}
                  style={{
                    fontSize: 6 * scale,
                    fontWeight: 600,
                    fill: SURFACE.textSecondary,
                    pointerEvents: 'none',
                  }}
                >
                  {m.city.length > 12 ? m.city.substring(0, 10) + '..' : m.city}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-10"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
        >
          <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap">
            <div className="font-semibold">{tooltip.label}</div>
            <div className="text-gray-300 mt-0.5">
              {tooltip.value.toLocaleString()} candidates ({((tooltip.value / total) * 100).toFixed(1)}%)
            </div>
          </div>
        </div>
      )}

      {/* Stats overlay */}
      <div
        className="absolute bottom-3 left-3 px-3 py-2 rounded-lg text-xs"
        style={{
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(4px)',
          border: `1px solid ${SURFACE.border}`,
        }}
      >
        <span style={{ color: SURFACE.textSecondary }}>
          {resolvedCount} locations mapped
          {unmatchedCount > 0 && <span className="ml-1" style={{ color: SURFACE.textMuted }}>({unmatchedCount} unresolved)</span>}
          {outlierCities > 0 && (
            <span className="ml-1" style={{ color: SURFACE.textMuted }}>
              · {outlierCount} candidate{outlierCount !== 1 ? 's' : ''} in {outlierCities} other location{outlierCities !== 1 ? 's' : ''} not shown
            </span>
          )}
        </span>
      </div>
    </div>
  );
});

GeoMap.displayName = 'GeoMap';

export default GeoMap;
